import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Form, Input, Layout, message, Image, Spin, Typography } from "antd";
import Logo from "../images/logo.png";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
const { Content, Footer } = Layout;
const { Title, Text } = Typography;

// Utility helpers for random numbers
const rnd = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * @typedef {Object} Light
 * @property {number} id
 * @property {number} top - percentage
 * @property {number} left - percentage
 * @property {number} size - px
 * @property {number} blur - px
 * @property {string} color - css color
 * @property {number} opacityA - base opacity
 * @property {number} opacityB - blink peak opacity
 * @property {number} floatDur - seconds
 * @property {number} blinkDur - seconds
 * @property {number} delay - seconds
 * @property {number} seed - used to force re-anim
 */

const palette = [
  "#1677ff",
  "#36cfc9",
  "#9254de",
  "#f759ab",
  "#13c2c2",
];

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [lights, setLights] = useState([]);

  const CONN_KEY = process.env.REACT_APP_NOT_SECRET_CODE_R?.trim();

  // Generate initial random lights
  useEffect(() => {
    const COUNT = 12; // tweak for density
    const arr = Array.from({ length: COUNT }).map((_, i) => ({
      id: i,
      top: rnd(8, 92),
      left: rnd(8, 92),
      size: rnd(180, 520),
      blur: rnd(60, 110),
      color: pick(palette),
      opacityA: rnd(0.18, 0.45),
      opacityB: rnd(0.55, 0.85),
      floatDur: rnd(8, 18),
      blinkDur: rnd(2.4, 4.8),
      delay: rnd(0, 6),
      seed: Math.random(),
    }));
    setLights(arr);

    // Randomly "blink" and reposition some lights periodically
    const t = setInterval(() => {
      setLights((prev) =>
        prev.map((l) => {
          if (Math.random() < 0.22) {
            return {
              ...l,
              top: rnd(8, 92),
              left: rnd(8, 92),
              size: rnd(160, 520),
              blur: rnd(60, 110),
              opacityA: rnd(0.16, 0.42),
              opacityB: rnd(0.58, 0.9),
              floatDur: rnd(8, 18),
              blinkDur: rnd(2.2, 4.6),
              delay: rnd(0, 6),
              color: pick(palette),
              seed: Math.random(), // nudge animation restart
            };
          }
          return l;
        })
      );
    }, 2600);
    return () => clearInterval(t);
  }, []);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      setError(false);

      const apiUrl = `${CONN_KEY}login.php`;
      const response = await axios.post(apiUrl, {
        username: values.username,
        password: values.password,
      });

      setLoading(false);

      if (response.data.status === 1) {
        localStorage.setItem("username", values.username);
        localStorage.setItem("_i_", response.data._i_);
        localStorage.setItem("permissions", JSON.stringify(response.data.permissions));
        window.location.href = '/Main';
      } else {
        message.error(response.data.message || 'Giriş alınmadı.');
      }
    } catch (error) {
      setLoading(false);
      setError(true);
      console.error(error);
      message.error('Serverə qoşularkən xəta baş verdi.');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#0b1220', position: 'relative', overflow: 'hidden' }}>
      {/* Dynamic light field */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
        {lights.map((l) => (
          <div
            key={l.id + '-' + l.seed}
            style={{
              position: 'absolute',
              top: `${l.top}%`,
              left: `${l.left}%`,
              width: l.size,
              height: l.size,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              background: `radial-gradient(closest-side, ${l.color}, transparent 70%)`,
              filter: `blur(${l.blur}px)`,
              // CSS variables allow per-light blink amplitudes
              // @ts-ignore
              ['--opa' ]: l.opacityA,
              // @ts-ignore
              ['--opb']: l.opacityB,
              opacity: l.opacityA,
              animation: `float3 ${l.floatDur}s ease-in-out ${l.delay}s infinite alternate, blink ${l.blinkDur}s ease-in-out ${l.delay / 2}s infinite`,
              pointerEvents: 'none',
            }}
          />
        ))}
      </div>

      <Content style={{ display: 'grid', placeItems: 'center', padding: '32px', position: 'relative', zIndex: 1 }}>
        {/* Login card */}
        <div className="login-card" style={{
          width: 'min(350px, 92%)',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 20,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* subtle glow */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', left: '50%', bottom: -40, transform: 'translateX(-50%)', width: 300, height: 120, background: 'radial-gradient(closest-side, rgba(22,119,255,.55), transparent 70%)', filter: 'blur(40px)', opacity: .5, animation: 'pulse 4s ease-in-out infinite' }} />
          </div>

          <div style={{ padding: '36px 34px 32px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Image preview={false} src={Logo} style={{ height: 52, animation: 'logoPop .9s ease' }} />
              <Title level={3} style={{ color: '#e6efff', marginBottom: 0 }}>Senate Group Admin</Title>
              <Text style={{ color: '#a6b3cf' }}>Hesabınıza daxil olun</Text>
            </div>

            <Form name="login_form" layout="vertical" onFinish={onFinish} style={{ marginTop: 18 }}>
              <Form.Item name="username" label={<span style={{ color: '#c9d1e8' }}>İstifadəçi adı</span>} rules={[{ required: true, message: "İstifadəçi adınızı daxil edin!" }]}>
                <div className="field-wrap">
                  <Input
                    size="large"
                    prefix={<UserOutlined style={{ color: '#7aa8ff' }} />}
                    placeholder="username"
                    autoComplete="username"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', color: '#e6efff' }}
                  />
                </div>
              </Form.Item>

              <Form.Item name="password" label={<span style={{ color: '#c9d1e8' }}>Şifrə</span>} rules={[{ required: true, message: "Şifrənizi daxil edin!" }]}>
                <div className="field-wrap">
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined style={{ color: '#7aa8ff' }} />}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', color: '#e6efff' }}
                  />
                </div>
              </Form.Item>

              <Form.Item style={{ marginBottom: 6 }}>
                <Spin spinning={loading} tip={<span style={{ color: '#e6efff' }}>Giriş edilir...</span>}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    disabled={loading}
                    className="primary-cta"
                    style={{
                      height: 48,
                      background: 'linear-gradient(90deg, #1677ff 0%, #36cfc9 100%)',
                      border: 'none',
                      boxShadow: '0 8px 20px rgba(22, 119, 255, .35)',
                      fontWeight: 600,
                      letterSpacing: .2,
                      transform: 'translateZ(0)'
                    }}
                  >
                    Giriş et
                  </Button>
                </Spin>
              </Form.Item>

              {error && <Text type="danger" style={{ color: '#ff7875' }}>Serverə qoşularkən xəta baş verdi.</Text>}
            </Form>
          </div>
        </div>
      </Content>

      <Footer style={{ textAlign: 'center', background: 'transparent', color: '#7a88a6' }}>
        © {new Date().getFullYear()} Senate Group — Bütün hüquqlar qorunur.
      </Footer>

      {/* Local animation styles */}
      <style>{`
        @keyframes float3 { 0% { transform: translate(-50%, -50%) translateY(0px) scale(1) } 50% { transform: translate(-50%, -50%) translateY(-14px) scale(1.025) } 100% { transform: translate(-50%, -50%) translateY(0px) scale(1) } }
        @keyframes blink { 0% { opacity: var(--opa) } 50% { opacity: var(--opb) } 100% { opacity: var(--opa) } }
        @keyframes pulse { 0%, 100% { opacity:.35; transform: translateX(-50%) scale(0.98) } 50% { opacity:.6; transform: translateX(-50%) scale(1.02) } }
        @keyframes logoPop { 0% { transform: translateY(8px) scale(.96); opacity: 0 } 100% { transform: translateY(0) scale(1); opacity: 1 } }
        .login-card .ant-form-item-label > label { color: #c9d1e8 !important; }
        .field-wrap { position: relative; border-radius: 12px; transition: box-shadow .25s ease, transform .15s ease; }
        .field-wrap:focus-within { box-shadow: 0 0 0 4px rgba(22,119,255,.25); transform: translateY(-1px); }
        .primary-cta { transition: transform .15s ease, box-shadow .2s ease, filter .2s ease; }
        .primary-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(22,119,255,.45); filter: brightness(1.03); }
        .primary-cta:active { transform: translateY(0); }
        @media (max-width: 480px) { .login-card { width: 92% !important; padding: 0 !important; } }
      `}</style>
    </Layout>
  );
};

export default Login;